import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../lib/api';
import { showToast } from './Toast';
import { formatCurrency, formatQuantity } from '../utils/formatters';

// ─────────────────────────────────────────────────────────────────────────────
// Carico di magazzino dagli ordini fornitore aperti.
// Si parte dal fornitore: la modale mostra le righe ancora da ricevere di tutti
// i suoi ordini EMESSI, così un solo carico (un DDT del fornitore) può coprire
// più ordini. Il movimento nasce già confermato; il residuo degli ordini e la
// loro chiusura li calcola il backend (services/caricoOrdiniFornitore.js).
// Si apre dalla scheda ordine fornitore e dalla lista movimenti di magazzino.
// ─────────────────────────────────────────────────────────────────────────────

interface FornitoreAperto {
  id: number;
  codice: string | null;
  ragioneSociale: string;
  ordiniAperti: number;
}

interface RigaAperta {
  id: number;
  ordinePos: number;
  articolo: { id: number; codice: string; descrizioneBase: string } | null;
  descrizione: string;
  unitaMisura: string | null;
  quantita: number;
  quantitaRicevuta: number;
  residuo: number;
  prezzoUnitarioAtteso: number | null;
  note: string | null;
  commessa: { id: number; numero: string } | null;
  caricabile: boolean;
}

interface OrdineAperto {
  id: number;
  numero: string | null;
  dataEmissione: string | null;
  note: string | null;
  sede: { id: number; nome: string; depositoPredefinitoId: number | null } | null;
  righe: RigaAperta[];
}

interface Opzione { id: number; nome?: string; codice?: string; descrizione?: string; isDefault?: boolean }

interface SceltaRiga { sel: boolean; quantita: string; prezzo: string }

interface Props {
  societaId: number;
  /** Fornitore già scelto (dalla scheda ordine): la combo è bloccata */
  fornitoreId?: number;
  fornitoreNome?: string;
  /** Ordine da cui si parte: le sue righe aperte arrivano già selezionate */
  ordineFornitoreId?: number;
  onClose: () => void;
  onCreated: (movimentoId: number) => void;
}

// "12,5" → 12.5; null se vuoto o non valido
function parseDecimale(s: string): number | null {
  if (!s.trim()) return null;
  const n = Number(s.trim().replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

function inInput(n: number | null): string {
  return n === null || n === undefined ? '' : String(n).replace('.', ',');
}

function oggiIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function estraiErrore(err: unknown, fallback: string): string {
  const payload = (err as { payload?: { error?: string | { message?: string } } })?.payload;
  const e = payload?.error;
  if (e && typeof e === 'object' && e.message) return e.message;
  if (typeof e === 'string' && e) return e;
  if (err instanceof Error && err.message && err.message !== '[object Object]') return err.message;
  return fallback;
}

export default function ModalCaricoOrdiniFornitore({
  societaId, fornitoreId: fornitoreFisso, fornitoreNome, ordineFornitoreId, onClose, onCreated,
}: Props) {
  const [fornitori, setFornitori] = useState<FornitoreAperto[]>([]);
  const [fornitoreId, setFornitoreId] = useState<string>(fornitoreFisso ? String(fornitoreFisso) : '');
  const [ordini, setOrdini] = useState<OrdineAperto[]>([]);
  const [depositi, setDepositi] = useState<Opzione[]>([]);
  const [causali, setCausali] = useState<Opzione[]>([]);
  const [scelte, setScelte] = useState<Record<number, SceltaRiga>>({});
  // Con il fornitore già scelto il caricamento parte subito: niente lampo di "nessun ordine"
  const [loading, setLoading] = useState(Boolean(fornitoreFisso));
  const [errore, setErrore] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  const [depositoId, setDepositoId] = useState('');
  const [causaleId, setCausaleId] = useState('');
  const [dataCarico, setDataCarico] = useState(oggiIso());
  const [ddtNumero, setDdtNumero] = useState('');
  const [ddtData, setDdtData] = useState(oggiIso());
  const [note, setNote] = useState('');

  // Fornitori con ordini aperti (solo se non arriva già scelto)
  useEffect(() => {
    if (fornitoreFisso) return;
    (async () => {
      try {
        // @api GET /api/movimenti-magazzino/carico-da-ordini-fornitore/fornitori?societaId= -> {"success":true,"data":[{"id":"number","codice":"string|null","ragioneSociale":"string","ordiniAperti":"number"}]}
        const r = await apiFetch<{ success: boolean; data: FornitoreAperto[] }>(
          `/api/movimenti-magazzino/carico-da-ordini-fornitore/fornitori?societaId=${societaId}`
        );
        setFornitori(r.data || []);
      } catch (err) {
        setErrore(estraiErrore(err, 'Errore nel caricamento dei fornitori'));
      }
    })();
  }, [societaId, fornitoreFisso]);

  // Ordini aperti del fornitore scelto
  useEffect(() => {
    if (!fornitoreId) { setOrdini([]); setScelte({}); return; }
    setLoading(true);
    setErrore(null);
    (async () => {
      try {
        // @api GET /api/movimenti-magazzino/carico-da-ordini-fornitore/ordini?societaId=&fornitoreId= -> {"success":true,"data":{"ordini":[{"id":"number","numero":"string|null","dataEmissione":"string|null","sede":{"id":"number","nome":"string","depositoPredefinitoId":"number|null"},"righe":[{"id":"number","ordinePos":"number","articolo":{"id":"number","codice":"string","descrizioneBase":"string"},"descrizione":"string","unitaMisura":"string|null","quantita":"number","quantitaRicevuta":"number","residuo":"number","prezzoUnitarioAtteso":"number|null","commessa":{"id":"number","numero":"string"},"caricabile":"boolean"}]}],"depositi":[{"id":"number","nome":"string","tipo":"string"}],"causali":[{"id":"number","codice":"string","descrizione":"string","isDefault":"boolean"}]}}
        const r = await apiFetch<{ success: boolean; data: { ordini: OrdineAperto[]; depositi: Opzione[]; causali: Opzione[] } }>(
          `/api/movimenti-magazzino/carico-da-ordini-fornitore/ordini?societaId=${societaId}&fornitoreId=${fornitoreId}`
        );
        const ord = r.data.ordini || [];
        setOrdini(ord);
        setDepositi(r.data.depositi || []);
        setCausali(r.data.causali || []);
        const iniziali: Record<number, SceltaRiga> = {};
        for (const o of ord) {
          for (const rg of o.righe) {
            iniziali[rg.id] = {
              sel: Boolean(ordineFornitoreId) && o.id === ordineFornitoreId && rg.caricabile && rg.residuo > 0,
              quantita: inInput(rg.residuo),
              prezzo: inInput(rg.prezzoUnitarioAtteso),
            };
          }
        }
        setScelte(iniziali);
        // Deposito: quello predefinito della sede dell'ordine da cui si parte (o del primo)
        const ordineBase = ord.find(o => o.id === ordineFornitoreId) || ord[0];
        const depPredefinito = ordineBase?.sede?.depositoPredefinitoId;
        const dep = (r.data.depositi || []).find(d => d.id === depPredefinito) || (r.data.depositi || [])[0];
        setDepositoId(dep ? String(dep.id) : '');
        setCausaleId(r.data.causali?.[0] ? String(r.data.causali[0].id) : '');
      } catch (err) {
        setErrore(estraiErrore(err, 'Errore nel caricamento degli ordini aperti'));
      } finally {
        setLoading(false);
      }
    })();
  }, [societaId, fornitoreId, ordineFornitoreId]);

  const setScelta = (rigaId: number, campo: keyof SceltaRiga, valore: string | boolean) => {
    setScelte(prev => ({ ...prev, [rigaId]: { ...prev[rigaId], [campo]: valore } }));
  };

  const righeCaricabili = (o: OrdineAperto) => o.righe.filter(rg => rg.caricabile && rg.residuo > 0);

  const selezionaOrdine = (o: OrdineAperto, sel: boolean) => {
    setScelte(prev => {
      const next = { ...prev };
      for (const rg of righeCaricabili(o)) next[rg.id] = { ...next[rg.id], sel };
      return next;
    });
  };

  const selezionate = useMemo(
    () => ordini.flatMap(o => o.righe.filter(rg => scelte[rg.id]?.sel)),
    [ordini, scelte]
  );

  const totaleValore = useMemo(() => selezionate.reduce((t, rg) => {
    const q = parseDecimale(scelte[rg.id]?.quantita || '');
    const p = parseDecimale(scelte[rg.id]?.prezzo || '');
    return t + (q !== null && p !== null ? q * p : 0);
  }, 0), [selezionate, scelte]);

  const conferma = async () => {
    if (!fornitoreId) { showToast('Scegli il fornitore', 'error'); return; }
    if (!depositoId) { showToast('Scegli il deposito di carico', 'error'); return; }
    if (!ddtNumero.trim()) { showToast('Indica il numero del DDT del fornitore', 'error'); return; }
    if (selezionate.length === 0) { showToast('Seleziona almeno una riga da caricare', 'error'); return; }
    const righe: { ordineFornitoreRigaId: number; quantita: number; prezzoUnitario: number | null }[] = [];
    for (const rg of selezionate) {
      const q = parseDecimale(scelte[rg.id].quantita);
      if (q === null || q <= 0) { showToast(`Quantità non valida: ${rg.descrizione}`, 'error'); return; }
      if (q > rg.residuo + 0.00005) {
        showToast(`${rg.descrizione}: ${formatQuantity(q)} è oltre il residuo (${formatQuantity(rg.residuo)})`, 'error');
        return;
      }
      const testoPrezzo = scelte[rg.id].prezzo;
      const p = testoPrezzo.trim() === '' ? null : parseDecimale(testoPrezzo);
      if (testoPrezzo.trim() !== '' && (p === null || p < 0)) { showToast(`Prezzo non valido: ${rg.descrizione}`, 'error'); return; }
      righe.push({ ordineFornitoreRigaId: rg.id, quantita: q, prezzoUnitario: p });
    }
    setSalvando(true);
    try {
      // @api POST /api/movimenti-magazzino/carico-da-ordini-fornitore {"societaId":"number","fornitoreId":"number","depositoId":"number","causaleId":"number|null","data":"string","numeroDocumentoEsterno":"string","dataDocumentoEsterno":"string|null","note":"string|null","righe":[{"ordineFornitoreRigaId":"number","quantita":"number","prezzoUnitario":"number|null"}]} -> {"success":true,"data":{"movimentoId":"number","numero":"number","anno":"number","ordini":["string"],"cambi":[{"ordineId":"number","numero":"string","da":"string","a":"string"}]}}
      const r = await apiFetch<{ success: boolean; data: { movimentoId: number; numero: number; anno: number; cambi: { numero: string; a: string }[] } }>(
        '/api/movimenti-magazzino/carico-da-ordini-fornitore',
        {
          method: 'POST',
          body: JSON.stringify({
            societaId,
            fornitoreId: parseInt(fornitoreId, 10),
            depositoId: parseInt(depositoId, 10),
            causaleId: causaleId ? parseInt(causaleId, 10) : null,
            data: dataCarico,
            numeroDocumentoEsterno: ddtNumero.trim(),
            dataDocumentoEsterno: ddtData || null,
            note: note.trim() || null,
            righe,
          }),
        }
      );
      const chiusi = (r.data.cambi || []).filter(c => c.a === 'CHIUSO').map(c => c.numero);
      showToast(
        `Carico ${r.data.numero}/${r.data.anno} confermato${chiusi.length ? ` — ordini ricevuti per intero: ${chiusi.join(', ')}` : ''}`,
        'success'
      );
      onCreated(r.data.movimentoId);
    } catch (err) {
      showToast(estraiErrore(err, 'Errore nella creazione del carico'), 'error');
      setSalvando(false);
    }
  };

  const nomeFornitore = fornitoreFisso
    ? fornitoreNome
    : fornitori.find(f => String(f.id) === fornitoreId)?.ragioneSociale;

  return (
    <div className="bop-modal-overlay" onClick={onClose}>
      <div className="bop-modal bop-modal--xl" onClick={e => e.stopPropagation()}>
        <div className="bop-modal__header">
          <h2 className="bop-modal__title">Carico merce da ordini fornitore{nomeFornitore ? ` — ${nomeFornitore}` : ''}</h2>
          <button className="bop-modal__close" onClick={onClose} aria-label="Chiudi">✕</button>
        </div>

        <div className="bop-modal__body">
          <div className="bop-carico-of__testata">
            {!fornitoreFisso && (
              <div className="bop-field bop-carico-of__testata-larga">
                <label className="bop-field__label" htmlFor="carico-of-fornitore">
                  Fornitore <span className="bop-field__required">*</span>
                </label>
                <select
                  id="carico-of-fornitore"
                  className="bop-field__select"
                  value={fornitoreId}
                  onChange={e => setFornitoreId(e.target.value)}
                >
                  <option value="">{fornitori.length ? 'Scegli il fornitore…' : 'Nessun fornitore con ordini aperti'}</option>
                  {fornitori.map(f => (
                    <option key={f.id} value={String(f.id)}>
                      {f.ragioneSociale} — {f.ordiniAperti} ordin{f.ordiniAperti === 1 ? 'e' : 'i'} apert{f.ordiniAperti === 1 ? 'o' : 'i'}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="bop-field">
              <label className="bop-field__label" htmlFor="carico-of-ddt">
                DDT del fornitore n. <span className="bop-field__required">*</span>
              </label>
              <input id="carico-of-ddt" className="bop-field__input" value={ddtNumero} onChange={e => setDdtNumero(e.target.value)} />
            </div>
            <div className="bop-field">
              <label className="bop-field__label" htmlFor="carico-of-ddt-data">Data del DDT</label>
              <input id="carico-of-ddt-data" type="date" className="bop-field__input" value={ddtData} onChange={e => setDdtData(e.target.value)} />
            </div>
            <div className="bop-field">
              <label className="bop-field__label" htmlFor="carico-of-data">
                Data del carico <span className="bop-field__required">*</span>
              </label>
              <input id="carico-of-data" type="date" className="bop-field__input" value={dataCarico} onChange={e => setDataCarico(e.target.value)} />
            </div>
            <div className="bop-field">
              <label className="bop-field__label" htmlFor="carico-of-deposito">
                Deposito <span className="bop-field__required">*</span>
              </label>
              <select id="carico-of-deposito" className="bop-field__select" value={depositoId} onChange={e => setDepositoId(e.target.value)}>
                <option value="">Scegli…</option>
                {depositi.map(d => <option key={d.id} value={String(d.id)}>{d.nome}</option>)}
              </select>
            </div>
            <div className="bop-field">
              <label className="bop-field__label" htmlFor="carico-of-causale">Causale</label>
              <select id="carico-of-causale" className="bop-field__select" value={causaleId} onChange={e => setCausaleId(e.target.value)}>
                {causali.length === 0 && <option value="">Nessuna causale di carico</option>}
                {causali.map(c => <option key={c.id} value={String(c.id)}>{c.codice} — {c.descrizione}</option>)}
              </select>
            </div>
            <div className="bop-field bop-carico-of__testata-larga">
              <label className="bop-field__label" htmlFor="carico-of-note">Note</label>
              <input id="carico-of-note" className="bop-field__input" value={note} onChange={e => setNote(e.target.value)} placeholder="Es. merce danneggiata, riserve sul DDT…" />
            </div>
          </div>

          {errore && <div className="bop-alert bop-alert--danger">{errore}</div>}

          {!fornitoreId ? (
            <div className="bop-empty-state">Scegli un fornitore per vedere i suoi ordini aperti.</div>
          ) : loading ? (
            <div className="bop-empty-state">Caricamento ordini aperti…</div>
          ) : ordini.length === 0 ? (
            <div className="bop-empty-state">Il fornitore non ha ordini aperti da ricevere.</div>
          ) : (
            <table className="bop-table bop-carico-of__tabella">
              <thead>
                <tr>
                  <th className="bop-table__th--select"></th>
                  <th>Pos</th>
                  <th>Articolo</th>
                  <th>U.M.</th>
                  <th className="bop-text-right">Ordinato</th>
                  <th className="bop-text-right">Già ricevuto</th>
                  <th className="bop-text-right">Ricevo ora</th>
                  <th className="bop-text-right">Prezzo unit.</th>
                </tr>
              </thead>
              <tbody>
                {ordini.map(o => {
                  const caricabili = righeCaricabili(o);
                  const tutteSel = caricabili.length > 0 && caricabili.every(rg => scelte[rg.id]?.sel);
                  return (
                    <React.Fragment key={o.id}>
                      <tr className="bop-carico-of__ordine">
                        <td className="bop-table__td--select">
                          <input
                            type="checkbox"
                            checked={tutteSel}
                            disabled={caricabili.length === 0}
                            onChange={e => selezionaOrdine(o, e.target.checked)}
                            aria-label={`Seleziona tutte le righe dell'ordine ${o.numero ?? o.id}`}
                          />
                        </td>
                        <td colSpan={7}>
                          Ordine {o.numero ?? `#${o.id}`}
                          {o.dataEmissione && <span className="bop-carico-of__sub-inline"> · del {new Date(o.dataEmissione).toLocaleDateString('it-IT')}</span>}
                          {o.sede && <span className="bop-carico-of__sub-inline"> · {o.sede.nome}</span>}
                        </td>
                      </tr>
                      {o.righe.map(rg => {
                        const s = scelte[rg.id];
                        const attiva = rg.caricabile && rg.residuo > 0;
                        return (
                          <tr key={rg.id} className={attiva ? undefined : 'bop-carico-of__riga--spenta'}>
                            <td className="bop-table__td--select">
                              <input
                                type="checkbox"
                                checked={Boolean(s?.sel)}
                                disabled={!attiva}
                                onChange={e => setScelta(rg.id, 'sel', e.target.checked)}
                                aria-label={`Carica la riga ${rg.ordinePos} dell'ordine ${o.numero ?? o.id}`}
                              />
                            </td>
                            <td>{rg.ordinePos}</td>
                            <td>
                              {rg.articolo ? <span className="bop-carico-of__codice">{rg.articolo.codice}</span> : null} {rg.descrizione}
                              {!rg.caricabile && <span className="bop-carico-of__sub">Senza articolo a magazzino: non si carica</span>}
                              {rg.caricabile && rg.residuo <= 0 && <span className="bop-carico-of__sub">Ricevuta per intero</span>}
                              {rg.commessa && <span className="bop-carico-of__sub">Commessa {rg.commessa.numero}</span>}
                            </td>
                            <td>{rg.unitaMisura || <span className="bop-text-muted">—</span>}</td>
                            <td className="bop-text-right">{formatQuantity(rg.quantita)}</td>
                            <td className="bop-text-right">{rg.quantitaRicevuta ? formatQuantity(rg.quantitaRicevuta) : <span className="bop-text-muted">—</span>}</td>
                            <td className="bop-text-right">
                              {attiva ? (
                                <input
                                  className="bop-field__input bop-carico-of__num"
                                  value={s?.quantita ?? ''}
                                  disabled={!s?.sel}
                                  onChange={e => setScelta(rg.id, 'quantita', e.target.value)}
                                  aria-label={`Quantità ricevuta riga ${rg.ordinePos} dell'ordine ${o.numero ?? o.id}`}
                                />
                              ) : <span className="bop-text-muted">—</span>}
                            </td>
                            <td className="bop-text-right">
                              {attiva ? (
                                <input
                                  className="bop-field__input bop-carico-of__num"
                                  value={s?.prezzo ?? ''}
                                  disabled={!s?.sel}
                                  placeholder="0,00"
                                  onChange={e => setScelta(rg.id, 'prezzo', e.target.value)}
                                  aria-label={`Prezzo di carico riga ${rg.ordinePos} dell'ordine ${o.numero ?? o.id}`}
                                />
                              ) : <span className="bop-text-muted">—</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}

          {selezionate.length > 0 && (
            <div className="bop-carico-of__totale">
              <span>{selezionate.length} rig{selezionate.length === 1 ? 'a' : 'he'} da caricare</span>
              <span>Valore {formatCurrency(totaleValore)}</span>
            </div>
          )}
          <p className="bop-field__hint">
            Il carico nasce confermato e aggiorna subito le giacenze. Il prezzo proposto è quello dell'ordine: correggilo con quello del DDT o della fattura del fornitore.
            Un ordine ricevuto per intero si chiude da solo; per chiuderlo con un residuo usa "Chiudi con residuo" nella scheda dell'ordine.
          </p>
        </div>

        <div className="bop-modal__footer">
          <button className="bop-btn bop-btn-secondary" onClick={onClose} disabled={salvando}>Annulla</button>
          <button className="bop-btn bop-btn-primary" onClick={conferma} disabled={salvando || selezionate.length === 0}>
            {salvando ? 'Carico in corso…' : 'Carica merce'}
          </button>
        </div>
      </div>
    </div>
  );
}
